import { isJwtAuth } from '../auth/authMode';

const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'
).replace(/\/$/, '');

function extractErrorMessage(errorPayload, status) {
  const fallback = `Request failed (${status})`;
  if (!errorPayload || typeof errorPayload !== 'object') {
    return fallback;
  }
  if (typeof errorPayload.message === 'string' && errorPayload.message.trim()) {
    return errorPayload.message;
  }
  if (typeof errorPayload.detail === 'string' && errorPayload.detail.trim()) {
    return errorPayload.detail;
  }
  return fallback;
}

async function parseResponse(response) {
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const errorPayload = await response.json();
      message = extractErrorMessage(errorPayload, response.status);
    } catch {
      // keep default
    }
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }
  if (response.status === 204) {
    return null;
  }
  return response.json();
}

async function publicRequest(path, { method = 'GET', body } = {}) {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  return parseResponse(response);
}

async function authedRequest(path, { method = 'GET', body, accessToken } = {}) {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  return parseResponse(response);
}

/** `workspaceType` (originator | provider | custodian) is the role the person
 *  signs in as; the backend refuses the login without a matching membership. */
export function login(email, password, workspaceType) {
  return publicRequest('/v1/auth/login', {
    method: 'POST',
    body: {
      email,
      password,
      ...(workspaceType ? { workspace_type: workspaceType } : {}),
    },
  });
}

export function entraLogin(accessToken, workspaceType) {
  return publicRequest('/v1/auth/entra/login', {
    method: 'POST',
    body: {
      access_token: accessToken,
      ...(workspaceType ? { workspace_type: workspaceType } : {}),
    },
  });
}

export function fetchAuthProviders() {
  return publicRequest('/v1/auth/providers');
}

export function createSession({ organizationId, loginToken, accessToken }) {
  return authedRequest('/v1/auth/session', {
    method: 'POST',
    body: {
      organization_id: organizationId,
      login_token: loginToken || undefined,
    },
    accessToken,
  });
}

export function refreshSession(refreshToken) {
  return publicRequest('/v1/auth/refresh', {
    method: 'POST',
    body: { refresh_token: refreshToken },
  });
}

export function logout(refreshToken) {
  return publicRequest('/v1/auth/logout', {
    method: 'POST',
    body: { refresh_token: refreshToken || null },
  });
}

export function fetchMe(accessToken) {
  return authedRequest('/v1/auth/me', { accessToken });
}

export function previewInvitation(token) {
  return publicRequest(`/v1/auth/invitations/${encodeURIComponent(token)}`);
}

export function acceptInvitation(token, { fullName, password }) {
  return publicRequest(`/v1/auth/invitations/${encodeURIComponent(token)}/accept`, {
    method: 'POST',
    body: { full_name: fullName, password },
  });
}

export function authApiEnabled() {
  return isJwtAuth();
}
